// Stale weighted-average-cost (WAC) reminder — RP-6 registry + SC-1 data wiring.
//
// Surfaces the count of active materials whose WAC snapshot is older than
// the staleness threshold (via `useMfgDockSignals` → costing summary API).
// R3: renders `null` when count is zero or unknown.

import { useAuth } from "@/contexts/AuthContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { staleWacHasSignal } from "@/lib/manufacturing/staleWacCount";

export type ActivityDockStaleWacReminderSectionProps = {
  staleWacCount: number | null;
  onOpenMfgInventory?: () => void;
};

export function ActivityDockStaleWacReminderSection({
  staleWacCount,
  onOpenMfgInventory,
}: ActivityDockStaleWacReminderSectionProps) {
  const auth = useAuth();

  if (!auth.hasRole("manufacturing")) return null;
  if (!staleWacHasSignal(staleWacCount)) return null;

  const count = staleWacCount as number;

  return (
    <section
      aria-label="Stale WAC reminder"
      {...{ [FIREFLY_TARGET_ATTR]: "stale-wac-reminder" }}
      className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/60 dark:bg-amber-950/30"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
            Stale material costs
          </h3>
          <p className="mt-1 text-[12px] text-amber-900 dark:text-amber-200">
            {count} {count === 1 ? "material has" : "materials have"} a stale
            weighted-average cost. Review them in Manufacturing → Inventory.
          </p>
        </div>
        {onOpenMfgInventory ? (
          <button
            type="button"
            onClick={onOpenMfgInventory}
            className="shrink-0 rounded-lg bg-amber-700 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-800 dark:bg-amber-600 dark:hover:bg-amber-500"
          >
            Review in Inventory
          </button>
        ) : null}
      </div>
    </section>
  );
}
